import { useLiveQuery } from 'dexie-react-hooks';

import { settings } from '@/lib/db';
import { buildPublicKey, type LicensePayload, verifyLicense } from '@/lib/license';

/**
 * The licence file, stored as a setting so it travels with a backup and a
 * sync folder to every device the pilot uses — a licence is the pilot's, not
 * the machine's. Checked on every read rather than trusted once: the file is
 * data, and the signature is the only thing that makes it a licence.
 */
export const LICENSE_KEY = 'license.file';

export type LicenseState =
  | { status: 'loading' }
  /** This build carries no public key, so there is nothing to check a licence against. */
  | { status: 'unavailable' }
  | { status: 'none' }
  | { status: 'valid'; license: LicensePayload }
  | { status: 'invalid' };

export async function readLicense(): Promise<LicenseState> {
  if (buildPublicKey() === undefined) {
    return { status: 'unavailable' };
  }
  const text = await settings.get<string | null>(LICENSE_KEY, null);
  if (text === null) {
    return { status: 'none' };
  }
  const check = await verifyLicense(text);
  return check.ok ? { status: 'valid', license: check.license } : { status: 'invalid' };
}

export function useLicense(): LicenseState {
  return useLiveQuery(readLicense, [], { status: 'loading' });
}

/** @returns whether the file was a valid licence and has been stored. */
export async function installLicense(text: string): Promise<boolean> {
  const check = await verifyLicense(text);
  if (!check.ok) {
    return false;
  }
  await settings.set(LICENSE_KEY, text);
  return true;
}

export async function removeLicense(): Promise<void> {
  await settings.set(LICENSE_KEY, null);
}
